import { Button, Grid } from "@mui/material";
import AGSelect from "../../AGSelect";
import { useForm, useWatch } from "react-hook-form";
import { IUserItem } from "../types";
import AGInput from "../../AGInput";
import AGPhoneInput from "../../AGPhoneInput";
import { useEffect } from "react";
import { setAlert } from "../../../redux/slices/alertSlice";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../redux/store";
import AGSearchableSelect from "../../AGSearchableSelect";
import { stationsList } from "../../../utils/constants";

interface IUserInfo {
  onSaveAndNext: (data: any, tabNumber: number) => void;
  defaultData: IUserItem;
  isEdit: boolean;
}

function UserInfo({ onSaveAndNext, defaultData, isEdit }: Readonly<IUserInfo>) {
  const { userId, name, email, phoneNo, password, roleName, stations } =
    defaultData;

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      userId,
      name,
      email,
      phoneNo:
        isEdit && phoneNo
          ? phoneNo.startsWith("+")
            ? phoneNo
            : `+${phoneNo}`
          : "",
      password,
      confirmPassword: password,
      roleName,
      stations:
        isEdit && roleName === "RSCOfficer"
          ? typeof stations === "string"
            ? stations?.split(",") || []
            : Array.isArray(stations)
            ? stations
            : []
          : [],
    },
  });

  // Handle form updates when defaultData changes
  useEffect(() => {
    if (isEdit) {
      // In edit mode, reset the entire form with existing data
      reset({
        userId,
        name,
        email,
        phoneNo: phoneNo
          ? phoneNo.startsWith("+")
            ? phoneNo
            : `+${phoneNo}`
          : "",
        password,
        confirmPassword: password,
        roleName,
        stations:
          roleName === "RSCOfficer"
            ? typeof stations === "string"
              ? stations?.split(",") || []
              : Array.isArray(stations)
              ? stations
              : []
            : [],
      });
    } else {
      // In add mode, update individual fields if they have values (coming back from preview)
      if (defaultData.name) setValue("name", defaultData.name);
      if (defaultData.email) setValue("email", defaultData.email);
      if (defaultData.phoneNo) setValue("phoneNo", defaultData.phoneNo);
      if (defaultData.roleName) setValue("roleName", defaultData.roleName);
      if (defaultData.stations) {
        const stationsValue =
          typeof defaultData.stations === "string"
            ? defaultData.stations.split(",")
            : Array.isArray(defaultData.stations)
            ? defaultData.stations
            : [];
        setValue("stations", stationsValue);
      }
    }
  }, [
    defaultData,
    isEdit,
    phoneNo,
    password,
    roleName,
    stations,
    reset,
    setValue,
    userId,
    name,
    email,
  ]);

  const dispatch = useDispatch<AppDispatch>();
  const selectedRole = useWatch({ control, name: "roleName" });
  const passwordValue = watch("password");

  useEffect(() => {
    if (!isEdit) {
      dispatch(
        setAlert({
          message:
            getPasswordRules()?.pattern?.message ??
            getPasswordRules()?.minLength?.message,
          severity: "info",
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRole]);

  const isWarden = selectedRole === "RSCOfficer";

  const getPasswordRules = () => {
    if (isWarden) {
      return {
        required: "Password is required",
        pattern: {
          value: /^\d{6}$/,
          message: "Password must be exactly 6 digits",
        },
      };
    } else {
      return {
        required: "Password is required",
        minLength: {
          value: 6,
          message: "Password must be at least 6 characters",
        },
      };
    }
  };

  return (
    <Grid
      component="form"
      container
      rowSpacing={1}
      columnSpacing={2}
      paddingY={1}
      rowGap={2}
      onSubmit={handleSubmit((e) => onSaveAndNext(e, 1))}
    >
      <Grid item md={4} lg={3} xs={12}>
        <AGSelect
          name="roleName"
          label="User Type"
          control={control}
          error={errors.roleName?.message}
          options={[
            { label: "SystemAdministrator", value: "SystemAdministrator" },
            { label: "Finance", value: "Finance" },
            { label: "RSCOfficer", value: "RSCOfficer" },
          ]}
          rules={{ required: "User Type is required" }}
        />
      </Grid>
      <Grid item md={4} lg={3} xs={12}>
        <AGInput
          name="name"
          label="Name"
          control={control}
          error={errors.name?.message}
          rules={{ required: "Name is required" }}
        />
      </Grid>
      <Grid item md={4} lg={3} xs={12}>
        <AGInput
          name="email"
          label="Email"
          control={control}
          error={errors.email?.message}
          rules={{
            required: "Email is required",
            pattern: {
              value: /\S+@\S+\.\S+/,
              message: "Invalid email address",
            },
          }}
        />
      </Grid>
      <Grid item md={4} lg={3} xs={12}>
        <AGPhoneInput
          label="Phone Number"
          name="phoneNo"
          control={control}
          minLength={isEdit ? 10 : 11}
          maxLength={20}
          error={errors.phoneNo?.message}
          required
        />
      </Grid>
      {isWarden && (
        <Grid item md={4} lg={3} xs={12}>
          <AGSearchableSelect
            name="stations"
            label="Stations"
            control={control}
            options={stationsList}
            multiple
            required
          />
        </Grid>
      )}

      {!isEdit && (
        <>
          <Grid item md={4} lg={3} xs={12}>
            <AGInput
              name="password"
              label="Password"
              type="password"
              control={control}
              min={6}
              max={30}
              error={errors.password?.message}
              rules={getPasswordRules()}
            />
          </Grid>
          <Grid item md={4} lg={3} xs={12}>
            <AGInput
              name="confirmPassword"
              label="Confirm Password"
              type="password"
              min={6}
              max={30}
              control={control}
              error={errors.confirmPassword?.message}
              rules={{
                required: "Please confirm your password",
                validate: (value: string) =>
                  value === passwordValue || "Passwords do not match",
              }}
            />
          </Grid>
        </>
      )}

      <Grid item md={12} className="flex gap-4 justify-end w-full">
        <Button variant="contained" type="submit">
          Save & Continue
        </Button>
      </Grid>
    </Grid>
  );
}

export default UserInfo;
